import { Badge, Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DynamicIcon, iconNames, type IconName } from 'lucide-react/dynamic';
import { type InventoryItem } from '../../service/api';

interface PendingActivationCardProps {
  item: InventoryItem;
  onApprove?: (inventoryItemId: string) => void;
  onDeny?: (inventoryItemId: string) => void;
  loading?: boolean;
}

export function PendingActivationCard({ 
  item, 
  onApprove, 
  onDeny, 
  loading = false 
}: PendingActivationCardProps) {
  const { t } = useTranslation();

  const handleApprove = () => {
    if (onApprove) {
      onApprove(item._id);
    }
  };

  const handleDeny = () => {
    if (onDeny) {
      onDeny(item._id);
    }
  };

  return (
    <Card className="h-100">
      <Card.Body className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center">
            {item.item.icon && iconNames.includes(item.item.icon as IconName) && (
              <div className="me-2">
                <DynamicIcon name={item.item.icon as IconName} size={24} />
              </div>
            )}
            <h5 className="card-title mb-0">{item.item.title}</h5>
          </div>
          <Badge bg="warning">{t('inventory.status.pending_activation')}</Badge>
        </div>

        {item.item.description && (
          <p className="card-text text-muted small">
            {item.item.description}
          </p>
        )}

        <div className="mb-3">
          <p className="text-muted small mb-1">
            <strong>{t('inventory.quantity')}:</strong> {item.amount}
          </p>
          <p className="text-muted small mb-1">
            <strong>{t('inventory.requested_by')}:</strong>{' '}
            {typeof item.userId === 'object'
              ? `${item.userId.given_name} ${item.userId.family_name}`
              : 'User'
            }
          </p>
          {item.activationRequestedAt && (
            <p className="text-muted small mb-0">
              <strong>{t('inventory.requested_at')}:</strong> {' '}
              {new Date(item.activationRequestedAt).toLocaleString()}
            </p>
          )}
        </div>

        <div className="mt-auto d-flex gap-2">
          <Button
            variant="success"
            size="sm"
            onClick={handleApprove}
            disabled={loading}
            className="flex-grow-1"
          >
            {loading ? t('common.loading') : t('inventory.approve')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeny}
            disabled={loading}
            className="flex-grow-1"
          >
            {loading ? t('common.loading') : t('inventory.deny')}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}